import { DollarOutlined, EditOutlined } from '@ant-design/icons';
import { TextField } from '@refinedev/antd';
import { Button, Form, Skeleton, Typography } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ModalFormActions,
  ShowInfoGrid,
  ShowInfoItem,
} from '@/components/general/ShowInfo/ShowInfo';
import { ShowSection, ShowSubsection } from '@/components/general/ShowInfo/ShowSection';
import { useCountryContext } from '@/contexts/CountryContext';
import { usePermissionContext } from '@/contexts/PermissionContext';
import { useSelectScrollSearch } from '@/hooks/useSelectScrollSearch';
import {
  getPaymentMethodFieldsByType,
  PaymentMethodField,
  PaymentMethodTypeLabels,
} from '@/model/General/PaymentMethod';
import { Technician } from '@/model/General/Technician';
import {
  SERVICE_ORDER_STATUS,
  SERVICE_ORDER_TYPE,
  ServiceOrder,
} from '@/model/ServiceOrder/ServiceOrder';

import ServiceOrderBillingData from '../ServiceOrderForm/ServiceOrderBillingData';
import usePaymentMethods from '../ServiceOrderForm/usePaymentMethods';
import { formatBoolean, formatWithSuffix } from './serviceOrderShowHelpers';
import useInlineSectionEdit from './useInlineSectionEdit';

/** Labels legibles para los campos del método de pago. */
const PAYMENT_FIELD_LABELS: Record<PaymentMethodField, string> = {
  banco: 'Banco',
  nombre_cuenta: 'Titular',
  numero_cuenta: 'Nº de cuenta',
  numero_de_agencia: 'Número de agencia',
  cbu: 'CBU',
  alias_cbu: 'Alias CBU',
  cuit_cuil: 'CUIT/CUIL',
  clave: 'Clave',
  email_pago: 'Email de pago',
};

interface ServiceOrderShowBillingProps {
  serviceOrder: ServiceOrder;
  blockEdit: boolean;
  refetch: () => Promise<any>;
}

// El Show expande relaciones (currency/payment_method como objetos) pero el Form necesita el id.
const getId = (value: any): number | undefined =>
  value != null && typeof value === 'object' ? value.id : value;
const ServiceOrderShowBilling = (props: ServiceOrderShowBillingProps) => {
  const { t } = useTranslation();
  const { isBrasilSelected } = useCountryContext();
  const { serviceOrder, blockEdit, refetch } = props;
  const [isEditing, setIsEditing] = useState(false);
  const {
    formProps,
    record,
    startEditing,
    handleFinish,
    currencySelectProps,
    assignTechnicianPrice,
    technicianPaymentMethodsSelectProps,
    createPaymentMethod,
    clientPaymentMethodsSelectProps,
    createClientPaymentMethod,
    canEdit,
    isSaving,
  } = useServiceOrderShowBilling(serviceOrder, blockEdit, refetch, isEditing, setIsEditing);
  const editButton = canEdit ? (
    <Button size="small" icon={<EditOutlined />} onClick={startEditing}>
      {t('Editar')}
    </Button>
  ) : undefined;
  const getPaymentFieldLabel = (field: PaymentMethodField) =>
    field === 'cuit_cuil' && isBrasilSelected ? 'CPF/CNPJ' : PAYMENT_FIELD_LABELS[field];

  if (isEditing) {
    return (
      <ShowSection
        title={t('Detalles de la facturación')}
        className="ServiceOrderShowBilling"
        icon={<DollarOutlined />}>
        <Form {...formProps} initialValues={record} onFinish={handleFinish} layout="vertical">
          <ServiceOrderBillingData
            formProps={formProps}
            currencySelectProps={currencySelectProps}
            assignTechnicianPrice={assignTechnicianPrice}
            paymentMethodSelectProps={technicianPaymentMethodsSelectProps}
            onCreatePaymentMethod={createPaymentMethod}
            clientPaymentMethodSelectProps={clientPaymentMethodsSelectProps}
            onCreateClientPaymentMethod={createClientPaymentMethod}
          />
          <ModalFormActions onCancel={() => setIsEditing(false)} />
        </Form>
      </ShowSection>
    );
  }

  return (
    <ShowSection
      title={t('Detalles de la facturación')}
      className="ServiceOrderShowBilling"
      headerExtra={editButton}
      icon={<DollarOutlined />}>
      {isSaving ? (
        <Skeleton active />
      ) : (
        <>
          <ShowSubsection title={t('Tarifas')}>
            <ShowInfoGrid>
              <ShowInfoItem label={t('Precio por hora')}>
                <TextField
                  value={formatWithSuffix(
                    serviceOrder.hourly_rate,
                    `${serviceOrder.currency?.simbolo}/hs`,
                  )}
                />
              </ShowInfoItem>
              <ShowInfoItem label={t('Precio por kilómetro')}>
                <TextField
                  value={formatWithSuffix(
                    serviceOrder.kilometres_rate,
                    `${serviceOrder.currency?.simbolo}/km`,
                  )}
                />
              </ShowInfoItem>
            </ShowInfoGrid>
          </ShowSubsection>

          <ShowSubsection title={t('Horas y kilómetros')}>
            <ShowInfoGrid>
              <ShowInfoItem label={t('Horas')}>
                <TextField value={formatWithSuffix(serviceOrder.hours_estimated, 'hs')} />
              </ShowInfoItem>
              <ShowInfoItem label={t('Kilómetros recorridos')}>
                <TextField value={formatWithSuffix(serviceOrder.kilometres_estimated, 'km')} />
              </ShowInfoItem>
            </ShowInfoGrid>
          </ShowSubsection>

          <ShowSubsection title={t('Opciones de pago')}>
            <ShowInfoGrid>
              <ShowInfoItem label={t('Pagar servicio al técnico')}>
                <TextField value={formatBoolean(serviceOrder.technician_pay, t('Sí'), t('No'))} />
              </ShowInfoItem>
              <ShowInfoItem label={t('Facturar al cliente')}>
                <TextField value={formatBoolean(serviceOrder.customer_invoice, t('Sí'), t('No'))} />
              </ShowInfoItem>
              {serviceOrder.payment_method && (
                <ShowInfoItem label={t('Método de pago del técnico')} colSpan={2}>
                  <div>
                    <TextField
                      value={t(PaymentMethodTypeLabels[serviceOrder.payment_method.tipo])}
                    />
                    {getPaymentMethodFieldsByType(
                      serviceOrder.payment_method.tipo,
                      isBrasilSelected,
                    ).map(field => {
                      const paymentMethodValue = (
                        serviceOrder.payment_method as Record<string, any>
                      )?.[field];

                      if (paymentMethodValue == null || paymentMethodValue === '') return null;

                      return (
                        <div key={field}>
                          <Typography.Text type="secondary">
                            {t(getPaymentFieldLabel(field))}:{' '}
                          </Typography.Text>
                          <Typography.Text>{paymentMethodValue}</Typography.Text>
                        </div>
                      );
                    })}
                  </div>
                </ShowInfoItem>
              )}
              {serviceOrder.payment_method_client && (
                <ShowInfoItem label={t('Método de cobro al cliente')} colSpan={2}>
                  <div>
                    <TextField
                      value={t(PaymentMethodTypeLabels[serviceOrder.payment_method_client.tipo])}
                    />
                    {getPaymentMethodFieldsByType(
                      serviceOrder.payment_method_client.tipo,
                      isBrasilSelected,
                    ).map(field => {
                      const paymentMethodValue = (
                        serviceOrder.payment_method_client as Record<string, any>
                      )?.[field];

                      if (paymentMethodValue == null || paymentMethodValue === '') return null;

                      return (
                        <div key={field}>
                          <Typography.Text type="secondary">
                            {t(getPaymentFieldLabel(field))}:{' '}
                          </Typography.Text>
                          <Typography.Text>{paymentMethodValue}</Typography.Text>
                        </div>
                      );
                    })}
                  </div>
                </ShowInfoItem>
              )}
            </ShowInfoGrid>
          </ShowSubsection>

          <ShowSubsection title={t('Cubierto por Acronex')}>
            <ShowInfoGrid>
              <ShowInfoItem label={t('Horas cubiertas por Acronex')}>
                <TextField value={formatWithSuffix(serviceOrder.hours_covered, 'hs')} />
              </ShowInfoItem>
              <ShowInfoItem label={t('Kilómetros cubiertos por Acronex')}>
                <TextField value={formatWithSuffix(serviceOrder.kilometres_covered, 'km')} />
              </ShowInfoItem>
              <ShowInfoItem label={t('Comentarios / Gastos adicionales')} colSpan={2}>
                <TextField value={serviceOrder.aditional_comment ?? '-'} />
              </ShowInfoItem>
            </ShowInfoGrid>
          </ShowSubsection>
        </>
      )}
    </ShowSection>
  );
};

const useServiceOrderShowBilling = (serviceOrder, blockEdit, refetch, isEditing, setIsEditing) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissionContext();
  const record = useMemo(
    () => ({
      ...serviceOrder,
      currency: getId(serviceOrder.currency),
      payment_method: getId(serviceOrder.payment_method),
      payment_method_client: getId(serviceOrder.payment_method_client),
      hours_covered: serviceOrder.hours_covered ?? 0,
      kilometres_covered: serviceOrder.kilometres_covered ?? 0,
    }),
    [serviceOrder],
  );
  const { formProps, isSaving, startEditing, handleFinish } = useInlineSectionEdit({
    id: serviceOrder.id,
    record,
    isEditing,
    setIsEditing,
    refetch,
    successMessage: t('Orden de servicio actualizada correctamente'),
  });
  const companyId = getId(serviceOrder.company);
  const technician = serviceOrder.technician as Technician;
  const { selectProps: currencySelectProps } = useSelectScrollSearch(
    {
      resource: 'currencies',
      optionLabel: (item: any) => item.nombre + ' (' + item.abreviacion + ')',
      pagination: { mode: 'off', pageSize: 3000 },
      queryOptions: { enabled: isEditing },
    },
    { saveItem: true },
  );
  const {
    technicianPaymentMethodsSelectProps,
    createPaymentMethod,
    clientPaymentMethodsSelectProps,
    createClientPaymentMethod,
  } = usePaymentMethods(formProps.form, technician ?? null, companyId);
  const assignTechnicianPrice = useMemo(() => {
    if (!technician?.moneda) {
      return undefined;
    }

    const {
      external_hour_price,
      external_km_price,
      internal_hour_price,
      internal_km_price,
      moneda,
    } = technician;

    if (serviceOrder.type === SERVICE_ORDER_TYPE.INSTALACION) {
      return internal_hour_price || internal_km_price
        ? () =>
            formProps.form?.setFieldsValue({
              currency: moneda.id,
              hourly_rate: internal_hour_price,
              kilometres_rate: internal_km_price,
            })
        : undefined;
    }

    return external_hour_price || external_km_price
      ? () =>
          formProps.form?.setFieldsValue({
            currency: moneda.id,
            hourly_rate: external_hour_price,
            kilometres_rate: external_km_price,
          })
      : undefined;
  }, [technician, serviceOrder.type, formProps.form]);
  const canEdit =
    !blockEdit &&
    !isEditing &&
    !isSaving &&
    hasPermission('edit') &&
    serviceOrder.status === SERVICE_ORDER_STATUS.REALIZADA;

  return {
    formProps,
    record,
    startEditing,
    handleFinish,
    currencySelectProps,
    assignTechnicianPrice,
    technicianPaymentMethodsSelectProps,
    createPaymentMethod,
    clientPaymentMethodsSelectProps,
    createClientPaymentMethod,
    canEdit,
    isSaving,
  };
};

export default ServiceOrderShowBilling;
